class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.sortSelect = this.querySelector('[data-search-sort]');
    this.isOpen = false;
    this.abortController = null;
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener(
      'input',
      debounce((event) => this.onChange(event), 300)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.sortSelect?.addEventListener('change', this.onSortChange.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  disconnectedCallback() {
    this.abortSearch();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length < 2) {
      this.abortSearch();
      this.removeAttribute('results');
      this.removeAttribute('loading');
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onSortChange() {
    this.sortPredictiveResults();

    if (this.getQuery().length >= 2 && this.getAttribute('results') === 'true') {
      this.open();
    }
  }

  onFormSubmit(event) {
    const selectedLink = this.querySelector('[aria-selected="true"] a');

    if (!this.getQuery().length || selectedLink) {
      event.preventDefault();
    }

    if (selectedLink) selectedLink.click();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (searchTerm.length < 2) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.target !== this.input) return;

    if (this.getQuery().length < 2) {
      this.close();
      return;
    }

    if (event.code === 'ArrowUp') {
      event.preventDefault();
      this.switchOption('up');
    }

    if (event.code === 'ArrowDown') {
      event.preventDefault();
      this.switchOption('down');
    }
  }

  onKeydown(event) {
    if (event.target !== this.input) return;

    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    if (event.code === 'Enter' && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    }
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const options = Array.from(this.querySelectorAll('[role="option"]'));
    if (!options.length) return;

    const selectedElement = this.querySelector('[aria-selected="true"]');
    const selectedIndex = options.indexOf(selectedElement);
    let nextIndex;

    if (direction === 'up') {
      nextIndex = selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1;
    } else {
      nextIndex = selectedIndex >= options.length - 1 ? 0 : selectedIndex + 1;
    }

    selectedElement?.setAttribute('aria-selected', 'false');

    const activeElement = options[nextIndex];
    activeElement.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', activeElement.id);
    this.setLiveRegionText(activeElement.textContent);
    activeElement.scrollIntoView({ block: 'nearest' });
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );
    selectedOption?.click();
  }

  getSortMode() {
    return this.sortSelect?.selectedOptions[0]?.dataset.searchSortMode || 'relevance';
  }

  getDiscountPercentage(item) {
    const price = Number(item.dataset.searchPrice) || 0;
    const compareAtPrice = Number(item.dataset.searchCompareAtPrice) || 0;

    if (compareAtPrice <= price || compareAtPrice <= 0) return 0;

    return ((compareAtPrice - price) / compareAtPrice) * 100;
  }

  sortPredictiveResults() {
    const resultsList = this.predictiveSearchResults.querySelector('#predictive-search-results-list');
    if (!resultsList) return;

    const productItems = Array.from(resultsList.querySelectorAll('[data-search-original-index]'));
    if (productItems.length < 2) return;

    const sortMode = this.getSortMode();
    const getOriginalIndex = (item) => Number(item.dataset.searchOriginalIndex) || 0;
    const compareByOriginalIndex = (itemA, itemB) => getOriginalIndex(itemA) - getOriginalIndex(itemB);

    productItems.sort((itemA, itemB) => {
      if (sortMode === 'price-descending') {
        const priceDifference = Number(itemB.dataset.searchPrice) - Number(itemA.dataset.searchPrice);
        return priceDifference || compareByOriginalIndex(itemA, itemB);
      }

      if (sortMode === 'price-ascending') {
        const priceDifference = Number(itemA.dataset.searchPrice) - Number(itemB.dataset.searchPrice);
        return priceDifference || compareByOriginalIndex(itemA, itemB);
      }

      if (sortMode === 'discount-descending') {
        const discountDifference = this.getDiscountPercentage(itemB) - this.getDiscountPercentage(itemA);
        return discountDifference || compareByOriginalIndex(itemA, itemB);
      }

      return compareByOriginalIndex(itemA, itemB);
    });

    const footer = resultsList.querySelector('.predictive-search__footer');
    productItems.forEach((item) => resultsList.insertBefore(item, footer));
  }

  abortSearch() {
    if (!this.abortController) return;

    this.abortController.abort();
    this.abortController = null;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    this.abortSearch();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent(
        'resources[type]'
      )}=product&${encodeURIComponent('resources[limit]')}=6&section_id=predictive-search`,
      { signal: controller.signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!section) throw new Error('Predictive search section was not returned.');

        this.cachedResults[queryKey] = section.innerHTML;
        this.renderSearchResults(section.innerHTML, searchTerm);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;

        this.removeAttribute('loading');
        this.close();
        console.error(error);
      })
      .finally(() => {
        if (this.abortController === controller) this.abortController = null;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', '');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText.trim();

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    if (this.getQuery() !== searchTerm) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.sortPredictiveResults();
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const resultCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (resultCount) this.setLiveRegionText(resultCount.textContent);
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.abortSearch();
      this.input.value = '';
      this.removeAttribute('results');
      this.removeAttribute('loading');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    selected?.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
